import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FinxaiTrade } from "../../models/finxaiTrade";
import { FinxaiDataGroup } from "../../models/data-group";
import { FinxaiMktData } from "../../models/finxaiMktData";
import { setNewDataIds } from "./dataTableSlice";
import { AppDispatch } from "../store";
import { FinxaiStrategy } from "../../models/finxaiStrategy";
import logger from "../../utils/logger";
import { TableGroups } from "../../components/custom-data-table";

interface DataState {
  tradeOrders: FinxaiTrade[];
  marketData: FinxaiMktData[];
  groups: FinxaiDataGroup[];
  tableKey: number;
  strategies: FinxaiStrategy[];
  sortLayoutInitialized: boolean;
  marketDataGroups: TableGroups;
  messageStats: {
    tradeCount: number;
    marketDataCount: number;
    strategyCount: number;
  };
}

const initialState: DataState = {
  tradeOrders: [],
  marketData: [],
  sortLayoutInitialized: false,
  groups: [{ groupName: "All Data", tickers: ["alldata"] }],
  tableKey: 0,
  strategies: [],
  marketDataGroups: {},
  messageStats: {
    tradeCount: 0,
    marketDataCount: 0,
    strategyCount: 0,
  },
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setMarketDataTableGroups(state, action: PayloadAction<TableGroups>) {
      state.marketDataGroups = action.payload;
    },

    /// To handle grouped values that dont exist in market data array

    addMissingGroupValuesToMarketData: (
      state,
      action: PayloadAction<{ groupNames: string[] }>
    ) => {
      const { groupNames } = action.payload;

      // Find all existing security IDs in the marketData array
      const existingSecurityIds = new Set(
        state.marketData.map((item) => item.security_id)
      );

      // Identify group names (security IDs) that are not in the marketData
      const missingSecurityIds = groupNames.filter(
        (securityId) => !existingSecurityIds.has(securityId)
      );

      // Create new FinxaiMktData objects for the missing security IDs
      const newMarketData = missingSecurityIds.map((securityId) => ({
        security_id: securityId,
        bid_px: 0,
        bid_sz: 0,
        offer_px: 0,
        offer_sz: 0,
        last_traded_px: 0,
        last_traded_sz: 0,
      }));

      //logger.info("Length Before addition:", state.marketData!.length)

      // Add the new objects to the marketData array
      state.marketData.push(...newMarketData);
      //logger.info("Length After addition:", state.marketData!.length)
    },

    initializeDataSort: (
      state,
      action: PayloadAction<{ field: keyof FinxaiTrade; order: "asc" | "desc" }>
    ) => {
      if (!state.sortLayoutInitialized) {
        const { field, order } = action.payload;
        state.tradeOrders = state.tradeOrders.slice().sort((a, b) => {
          if (a[field] < b[field]) return order === "asc" ? -1 : 1;
          if (a[field] > b[field]) return order === "asc" ? 1 : -1;
          return 0;
        });
        state.sortLayoutInitialized = true;
      }
    },
    rearrangeTradeOrders: (state, action: PayloadAction<string[]>) => {
      const columnOrder = action.payload;
      state.tradeOrders = state.tradeOrders.map((trade) => {
        const rearrangedTrade: FinxaiTrade = {} as FinxaiTrade;
        columnOrder.forEach((columnName) => {
          if (columnName in trade) {
            rearrangedTrade[columnName] = trade[columnName];
          }
        });
        return rearrangedTrade;
      });
    },

    sortTradeOrders(
      state,
      action: PayloadAction<{ field: keyof FinxaiTrade; order: "asc" | "desc" }>
    ) {
      const { field, order } = action.payload;
      state.tradeOrders = state.tradeOrders.slice().sort((a, b) => {
        if (a[field] < b[field]) return order === "asc" ? -1 : 1;
        if (a[field] > b[field]) return order === "asc" ? 1 : -1;
        return 0;
      });
    },
    sortMarketData(
      state,
      action: PayloadAction<{
        field: keyof FinxaiMktData;
        order: "asc" | "desc";
      }>
    ) {
      const { field, order } = action.payload;
      state.marketData = state.marketData.slice().sort((a, b) => {
        if (a[field] < b[field]) return order === "asc" ? -1 : 1;
        if (a[field] > b[field]) return order === "asc" ? 1 : -1;
        return 0;
      });
    },

    setMarketData(state, action: PayloadAction<FinxaiMktData[]>) {
      state.marketData = action.payload;
    },

    addOrUpdateStrategy(state, action: PayloadAction<FinxaiStrategy>) {
      const strategy = action.payload;
      const securityId = strategy.instrument.security_id;
      // const tickerSymbol = strategy.instrument.ticker_symbol || "N.A";
         
      /// Add or update strategy in the strategies array
      const strategyIndex = state.strategies.findIndex( 
        (s) => s.instrument.security_id === securityId
      );


      if (strategyIndex !== -1) {
        state.strategies[strategyIndex] = strategy;
      } else {
        state.strategies.push(strategy);
      }

      //  Update ALL trade orders that match this security_id with the correct ticker_symbol

      // if (tickerSymbol !== "N.A") {
      //   state.tradeOrders.forEach((order) => {
      //     if (order.security_id === securityId) {
      //       // Update ticker symbol for matching orders
      //       order.ticker_symbol = tickerSymbol;
      //     }
      //   });
      // }

      // Add to marketData if not already present

      const marketDataIndex = state.marketData.findIndex(
        (data) => data.security_id === securityId
      );

      if (marketDataIndex === -1 && strategy.instrumentType === 4) {
        // Only add if CUSTOM
        const newMarketData: FinxaiMktData = {
          security_id: securityId,
          bid_px: 0,
          bid_sz: 0,
          offer_px: 0,
          offer_sz: 0,
          last_traded_px: 0,
          last_traded_sz: 0,
        };
        state.marketData.unshift(newMarketData);
      }
    },

    addOrUpdateMarketData(state, action: PayloadAction<FinxaiMktData>) {
      const { security_id, bid_px, offer_px } = action.payload;

      // Pre-calculate fixed values for comparison (only when needed)
      const bidPxFixed = Number(bid_px.toFixed(6));
      const offerPxFixed = Number(offer_px.toFixed(6));

      const index = state.marketData.findIndex(
        (data) => data.security_id === security_id
      );

      let marketDataChanged = false;


      if (index !== -1) {
        const existingData = state.marketData[index];
        // Check if bid or offer price has changed (using direct comparison with tolerance)
        const bidChanged = Math.abs(existingData.bid_px - bid_px) > 0.001;
        const offerChanged = Math.abs(existingData.offer_px - offer_px) > 0.001;

        if (bidChanged || offerChanged) {
          // Update only changed fields to avoid unnecessary object creation
          state.marketData[index] = {
            ...existingData,
            bid_px: bidPxFixed,
            offer_px: offerPxFixed,
            bid_sz: action.payload.bid_sz,
            offer_sz: action.payload.offer_sz,
            last_traded_px: action.payload.last_traded_px,
            last_traded_sz: action.payload.last_traded_sz,
          };
          marketDataChanged = true;
        }
      } else {
        // Only find strategy if we need to add new market data
        const strategy = state.strategies.find((s) => {
          return s.instrument.security_id === security_id;
        });

        if (strategy) {
          // Only add market data if the instrument type is CUSTOM
          if (strategy.instrumentType === 4) {
            // InstrumentType.INSTRUMENT_TYPE_CUSTOM
            state.marketData.unshift(action.payload);
            marketDataChanged = true;
          } else {
            // Log non-custom instruments for tracking
            console.log(
              `Skipping market data for non-custom instrument: ${security_id}, type: ${strategy.instrumentType}`
            );
          }
        }
      }

      // Update trade orders with new market data prices if security ID matches
      // Early exit if no change occurred
      if (!marketDataChanged) {
        return;
      }

      // Optimize trade orders update by updating in place
      /*for (let i = 0; i < state.tradeOrders.length; i++) {
        if (state.tradeOrders[i].security_id === security_id) {
          state.tradeOrders[i].bid_px = bidPxFixed;
          state.tradeOrders[i].offer_px = offerPxFixed;
        }
      }*/

      // Only reload if market data actually changed
      /*if (marketDataChanged) {
        dataSlice.caseReducers.reloadTradeOrders(state);
      }*/
    },
    setTradeOrders(state, action: PayloadAction<FinxaiTrade[]>) {
      state.tradeOrders = action.payload;
    },

    addOrUpdateTradeOrder(state, action: PayloadAction<FinxaiTrade>) {
      const payload = action.payload;

      if (!payload) {
        console.warn("addOrUpdateTradeOrder called with undefined payload — ignoring.");
        return;
      }
      const { order_id, show_client, security_id } = action.payload;
      
      /// If show_client is false, remove the order if it exists
      if (show_client === false) {
        const index = state.tradeOrders.findIndex(
          (order) => order.order_id === order_id
        );
        
        if (index !== -1) {
          console.log(`Removing trade order ${order_id} due to show_client=false`);
          state.tradeOrders.splice(index, 1);
        }
        return; /// Exit early, don't add or update
      }
      
      /// Find the matching strategy to get the correct ticker_symbol
      const strategy = state.strategies.find(
        (s) => s.instrument.security_id === security_id
      );
      // const tickerSymbol = strategy?.instrument.ticker_symbol || action.payload.ticker_symbol;
      
      const index = state.tradeOrders.findIndex(
        (order) => order.order_id === order_id
      );
    
      if (index !== -1) {
        const existingOrder = state.tradeOrders[index];

        /// Preserve the ticker_symbol if:
        /// 1. The new one is "N.A" but we have a valid existing one, OR
        /// 2. We found a valid strategy ticker_symbol
        const preservedTickerSymbol =
          strategy?.instrument.ticker_symbol ||
          (action.payload.ticker_symbol === "N.A" && existingOrder.ticker_symbol !== "N.A"
            ? existingOrder.ticker_symbol
            : action.payload.ticker_symbol);

        console.log(`Updating trade order ${order_id}, ticker: ${preservedTickerSymbol}`);
        state.tradeOrders[index] = {
          ...existingOrder,
          ...action.payload,
          ticker_symbol: preservedTickerSymbol,
        };
      } else {
        console.log(`Adding new trade order ${order_id}, ticker: ${action.payload.ticker_symbol}`);
        state.tradeOrders.unshift({
          ...action.payload,
        });
      }
    },

    setTableKey(state, action: PayloadAction<number>) {
      //state.tableKey = action.payload;
    },

    /// For creating grouped layouts of the grid
    setGroups(state, action: PayloadAction<FinxaiDataGroup[]>) {
      state.groups = action.payload;
    },
    addGroup(state, action: PayloadAction<FinxaiDataGroup>) {
      state.groups.unshift(action.payload);
    },
    /// For clearing in memory data on logout
    postLogoutDataCleanup(state) {
      state.marketData = [];
      state.tradeOrders = [];
      state.strategies = [];
      state.sortLayoutInitialized = false;
      state.messageStats = {
        tradeCount: 0,
        marketDataCount: 0,
        strategyCount: 0,
      };
    },

    /// Message count tracking
    incrementTradeMessageCount(state) {
      state.messageStats.tradeCount += 1;
    },
    incrementMarketDataMessageCount(state) {
      state.messageStats.marketDataCount += 1;
    },
    incrementStrategyMessageCount(state) {
      state.messageStats.strategyCount += 1;
    },
    resetMessageStats(state) {
      state.messageStats = {
        tradeCount: 0,
        marketDataCount: 0,
        strategyCount: 0,
      };
    },
  },
});

export const {
  postLogoutDataCleanup,
  setTradeOrders,
  rearrangeTradeOrders,
  addOrUpdateTradeOrder,
  addOrUpdateMarketData,
  addOrUpdateStrategy,
  setGroups,
  setTableKey,
  sortTradeOrders,
  sortMarketData,
  addGroup,
  setMarketData,
  initializeDataSort,
  setMarketDataTableGroups,
  addMissingGroupValuesToMarketData,
  incrementTradeMessageCount,
  incrementMarketDataMessageCount,
  incrementStrategyMessageCount,
  resetMessageStats,
} = dataSlice.actions;
export default dataSlice.reducer;
